// LuviaRx — Inventory: paste a stock list, preview the changes, then apply exact counts.
import React, { useState } from "react";
import axios from "axios";
import { ClipboardList, Loader2, CheckCircle2, AlertTriangle, Copy, Check, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const stockListText = (rows) =>
  (rows || []).map((r) => `${r.product_name} ${r.variant_id} ${r.stock}`).join("\n");

export default function BulkStockPaste({ token, rows, onApplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [copied, setCopied] = useState(false);

  const download = () => {
    const blob = new Blob([stockListText(rows) + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `luviarx-stock-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stockListText(rows));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { setError("Couldn't copy. Use Download instead."); }
  };

  const call = async (apply) => {
    setBusy(true); setError(""); setDone("");
    try {
      const { data } = await axios.post(`${API}/admin/inventory/bulk`, { text, apply },
        { headers: { Authorization: `Bearer ${token}` } });
      if (apply) {
        setDone(`Updated ${data.matches.length} SKU${data.matches.length === 1 ? "" : "s"}.`);
        setPreview(null); setText(""); setOpen(false);
        onApplied?.();
      } else setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not read the list");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="flex items-center justify-between flex-wrap gap-3">
        {done && <p data-testid="bulk-stock-done" className="text-sm text-sage-700 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{done}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button type="button" onClick={copy} disabled={!rows?.length} data-testid="stock-list-copy-btn"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-charcoal-200 text-charcoal-600 hover:border-[#8B2252]/40 hover:text-[#8B2252] transition-colors disabled:opacity-40">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy stock list"}
          </button>
          <button type="button" onClick={download} disabled={!rows?.length} data-testid="stock-list-download-btn"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-charcoal-200 text-charcoal-600 hover:border-[#8B2252]/40 hover:text-[#8B2252] transition-colors disabled:opacity-40">
            <Download className="w-4 h-4" /> Download stock list
          </button>
          <button type="button" onClick={() => { setOpen(true); setError(""); }} data-testid="bulk-stock-open-btn"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-charcoal-200 text-charcoal-600 hover:border-[#8B2252]/40 hover:text-[#8B2252] transition-colors">
            <ClipboardList className="w-4 h-4" /> Bulk update from a list
          </button>
        </div>
      </div>
    );
  }

  const changed = preview?.matches.filter((m) => m.current_stock !== m.new_stock).length ?? 0;

  return (
    <div data-testid="bulk-stock-panel" className="surface-card p-6 space-y-4">
      <div>
        <p className="overline mb-1">Bulk update stock</p>
        <p className="text-sm text-charcoal-500">
          Paste product, size and the exact count, e.g. <span className="font-mono text-xs">GLP-3R 10mg 59</span>. One per line,
          or all on one line. Counts replace the current stock. Products you leave out stay the same.
        </p>
        <button type="button" onClick={() => { setText(stockListText(rows)); setPreview(null); }} data-testid="bulk-stock-load-current-btn"
          className="mt-2 text-xs text-[#8B2252] hover:underline">
          Start from current stock
        </button>
      </div>
      <textarea data-testid="bulk-stock-input" value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }}
        rows={6} className="input font-mono text-xs" placeholder={"GLP-3R 10mg 59\nNAD+ 500mg 8\nBac Water 3mL 12"} />
      {error && <p data-testid="bulk-stock-error" className="text-sm text-red-600">{error}</p>}

      {preview && (
        <div data-testid="bulk-stock-preview" className="space-y-3">
          {preview.problems.length > 0 && (
            <div data-testid="bulk-stock-problems" className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 space-y-1">
              <p className="font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Please double-check these lines (they won't be saved):</p>
              {preview.problems.map((p) => <p key={p}>• {p}</p>)}
            </div>
          )}
          <div className="max-h-72 overflow-y-auto rounded-2xl border border-charcoal-100">
            <table className="min-w-full text-sm">
              <thead className="bg-cream-200/70 text-charcoal-500 text-xs uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2">Product</th><th className="text-left px-4 py-2">Size</th><th className="text-right px-4 py-2">Now</th><th className="text-right px-4 py-2">New</th></tr>
              </thead>
              <tbody>
                {preview.matches.map((m) => (
                  <tr key={`${m.product_id}-${m.variant_id}`} data-testid={`bulk-stock-row-${m.product_id}-${m.variant_id}`} className="border-t border-charcoal-100/50">
                    <td className="px-4 py-1.5 text-charcoal-600">{m.product_name}</td>
                    <td className="px-4 py-1.5 text-charcoal-500">{m.variant_label}</td>
                    <td className="px-4 py-1.5 text-right text-charcoal-400">{m.current_stock}</td>
                    <td className={`px-4 py-1.5 text-right font-semibold ${m.current_stock !== m.new_stock ? "text-[#8B2252]" : "text-charcoal-400"}`}>{m.new_stock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-charcoal-400">{preview.matches.length} matched · {changed} will change</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {!preview ? (
          <button type="button" disabled={busy || !text.trim()} onClick={() => call(false)} data-testid="bulk-stock-preview-btn" className="btn-primary inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Check list
          </button>
        ) : (
          <button type="button" disabled={busy || preview.matches.length === 0} onClick={() => call(true)} data-testid="bulk-stock-apply-btn" className="btn-primary inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save {preview.matches.length} stock count{preview.matches.length === 1 ? "" : "s"}
          </button>
        )}
        <button type="button" onClick={() => { setOpen(false); setPreview(null); setError(""); }} data-testid="bulk-stock-cancel-btn" className="btn-ghost">Close</button>
      </div>
    </div>
  );
}
